import type {
  Note,
  PcmFrameListener,
  SessionStatus,
  StartOptions,
  UploadEventMap,
  UploadHandle,
  VoxClient,
  VoxEventMap,
} from '@voxhq/web-sdk';
import type { VoxSdkModule } from './vox-sdk';

/**
 * The per-upload options the real `uploadFile` accepts, derived from the vendor
 * typings so the fakes below stay in step whenever the vendor adds an option.
 */
export type VoxUploadOptions = NonNullable<
  Parameters<VoxSdkModule['uploadFile']>[3]
>;

// One Set holds every event's listeners, so their argument types are erased.
// The generic index `TEventMap[TEvent]` forces that erasure through `unknown`.
type ErasedListener = (...args: never[]) => void;

/** A minimal typed event emitter, so the two fakes below don't duplicate it. */
class MockEventEmitter<TEventMap extends Record<string, unknown[]>> {
  private readonly _listeners = new Map<keyof TEventMap, Set<ErasedListener>>();

  on<TEvent extends keyof TEventMap>(
    event: TEvent,
    listener: (...args: TEventMap[TEvent]) => void
  ): () => void {
    const listeners = this._listeners.get(event) ?? new Set<ErasedListener>();
    listeners.add(listener as unknown as ErasedListener);
    this._listeners.set(event, listeners);
    return () => this.off(event, listener);
  }

  off<TEvent extends keyof TEventMap>(
    event: TEvent,
    listener: (...args: TEventMap[TEvent]) => void
  ): void {
    this._listeners.get(event)?.delete(listener as unknown as ErasedListener);
  }

  emit<TEvent extends keyof TEventMap>(
    event: TEvent,
    ...args: TEventMap[TEvent]
  ): void {
    const listeners = this._listeners.get(event);
    if (!listeners) {
      return;
    }
    for (const listener of [...listeners]) {
      (listener as unknown as (...eventArgs: TEventMap[TEvent]) => void)(
        ...args
      );
    }
  }

  clear(): void {
    this._listeners.clear();
  }
}

/**
 * Dependency-free fake of the SDK's live-recording `VoxClient`. It records the
 * control calls made against it and lets a test drive `notes` / `status` /
 * `error` events by hand, so consumers can assert how they respond without the
 * real browser SDK. Reused by the Jest manual mock, by consumer unit tests
 * (Todo 7), and by the Cypress override seam (Todo 15).
 */
export class MockVoxClient implements VoxClient {
  private readonly _events = new MockEventEmitter<VoxEventMap>();
  private readonly _analyzerBuffers = new Map<number, Float32Array>();

  /** The session id passed to the most recent `start()`, for assertions. */
  startedSessionId: string | undefined;
  /** The options passed to the most recent `start()`, for assertions. */
  lastStartOptions: StartOptions | undefined;
  /** Whether `stop()` has been called. */
  stopped = false;
  /** Whether `close()` has been called. */
  closed = false;

  on<TEvent extends keyof VoxEventMap>(
    event: TEvent,
    listener: (...args: VoxEventMap[TEvent]) => void
  ): () => void {
    return this._events.on(event, listener);
  }

  off<TEvent extends keyof VoxEventMap>(
    event: TEvent,
    listener: (...args: VoxEventMap[TEvent]) => void
  ): void {
    this._events.off(event, listener);
  }

  start(sessionId: string, options?: StartOptions): void {
    this.startedSessionId = sessionId;
    this.lastStartOptions = options;
  }

  stop(): void {
    this.stopped = true;
  }

  close(): void {
    this.closed = true;
    this._events.clear();
  }

  /**
   * Satisfies the realtime PCM tap `VoxClient` gained in 0.0.9. The app does
   * not use realtime STT, so the fake is a no-op. The `null` mirrors the
   * vendor's signature (third-party contract).
   */
  setPcmFrameListener(_listener: PcmFrameListener | null): void {
    return;
  }

  /**
   * Mirrors the real SDK's analyser, which allocates ONE buffer per band count
   * and mutates it in place on every call — consumers must copy the result if
   * they need value semantics. Reusing the instance here keeps that hazard
   * reproducible in unit tests.
   */
  getAnalyzerBandLevels(bands: number): Float32Array {
    const buffer = this._analyzerBuffers.get(bands) ?? new Float32Array(bands);
    this._analyzerBuffers.set(bands, buffer);
    return buffer;
  }

  /** Test control: overwrite every reused analyser buffer in place, mimicking
   *  the real SDK mutating its cached buffer as new audio arrives. */
  setAnalyzerLevel(level: number): void {
    for (const buffer of this._analyzerBuffers.values()) {
      buffer.fill(level);
    }
  }

  /** Test control: emit a `notes` event to every current subscriber. */
  emitNotes(notes: Note[]): void {
    this._events.emit('notes', notes);
  }

  /** Test control: emit a `status` event to every current subscriber. */
  emitStatus(status: SessionStatus): void {
    this._events.emit('status', status);
  }

  /** Test control: emit an `error` event to every current subscriber. */
  emitError(error: Error): void {
    this._events.emit('error', error);
  }
}

/**
 * Dependency-free fake of the SDK's `uploadFile` return handle, so the mock
 * module's `uploadFile` returns something a test can drive and close.
 */
export class MockUploadHandle implements UploadHandle {
  private readonly _events = new MockEventEmitter<UploadEventMap>();

  /** Whether `close()` has been called. */
  closed = false;
  /**
   * The options the most recent `uploadFile()` was called with — `baseUrl`,
   * `context` and `recordedAt` among them — so an upload test can assert what
   * the app asked the vendor for without spying on the module.
   */
  lastUploadOptions: VoxUploadOptions | undefined;

  on<TEvent extends keyof UploadEventMap>(
    event: TEvent,
    listener: (...args: UploadEventMap[TEvent]) => void
  ): () => void {
    return this._events.on(event, listener);
  }

  off<TEvent extends keyof UploadEventMap>(
    event: TEvent,
    listener: (...args: UploadEventMap[TEvent]) => void
  ): void {
    this._events.off(event, listener);
  }

  close(): void {
    this.closed = true;
    this._events.clear();
  }

  /** Test control: emit a `notes` event to every current subscriber. */
  emitNotes(notes: Note[]): void {
    this._events.emit('notes', notes);
  }

  /** Test control: emit a `status` event to every current subscriber. */
  emitStatus(status: SessionStatus): void {
    this._events.emit('status', status);
  }

  /** Test control: emit an `error` event to every current subscriber. */
  emitError(error: Error): void {
    this._events.emit('error', error);
  }

  /** Test control: emit a `progress` event to every current subscriber. */
  emitProgress(loaded: number, total: number): void {
    this._events.emit('progress', { loaded, total });
  }
}

/** A mock SDK module plus the fakes it hands out, so tests can drive events. */
export interface IMockVoxSdk {
  /** The `{ initializeVox, uploadFile }` module, shaped like the real SDK. */
  module: VoxSdkModule;
  /** The client every `initializeVox()` call returns. */
  client: MockVoxClient;
  /** The handle every `uploadFile()` call returns. */
  uploadHandle: MockUploadHandle;
}

/**
 * Build a fake SDK module whose `initializeVox` / `uploadFile` always hand back
 * the same controllable fakes, so a test can subscribe through the wrapper and
 * then emit events on the returned `client` / `uploadHandle`.
 */
export function createMockVoxSdk(): IMockVoxSdk {
  const client = new MockVoxClient();
  const uploadHandle = new MockUploadHandle();
  const module: VoxSdkModule = {
    initializeVox: () => client,
    uploadFile: (_token, _sessionId, _file, options) => {
      uploadHandle.lastUploadOptions = options;
      return uploadHandle;
    },
  };
  return { module, client, uploadHandle };
}
